<template>
  <div class="chat-bot-wrapper" :style="`height: ${$vuetify.breakpoint.height - ($vuetify.breakpoint.xs ? 56 : 64)}px`">
    <v-container class="chat-bot-container pt-0">
      <v-form v-model="valid" class="fill-height d-flex flex-column">
        <div class="chat-content flex-grow-1 overflow-y-auto pr-1 pb-2">
          <div v-if="!chatId && !chatStore.loadingChatId && !chatStore.waitingForAssistantMessage">
            <h1 :class="$vuetify.breakpoint.mobile ? 'mt-7 font-weight-black' : 'mt-11 font-weight-black'">
              {{ mode === "rewriteScript" ? $t("readyTo") : $t("readyToCreate") }}
              <span class="boujee-text"
                >{{
                  mode === "ideas"
                    ? $t("unlimitedVideoIdeas").toLowerCase()
                    : mode === "script" && options.scriptTemplate === "outline"
                    ? $t("videoScriptOutline").toLowerCase()
                    : mode === "script"
                    ? $t("videoScript").toLowerCase()
                    : mode === "rewriteScript"
                    ? $t("rewriteScriptOfAnyVideo").toLowerCase()
                    : mode === "title"
                    ? $t("videoTitle").toLowerCase()
                    : mode === "description"
                    ? $t("videoDescription").toLowerCase()
                    : mode === "tags"
                    ? $t("videoTags").toLowerCase()
                    : mode === "chapters"
                    ? $t("videoChapters").toLowerCase()
                    : ""
                }} </span
              >{{ $t("inSeconds") }}?
            </h1>
            <p class="text--secondary">
              {{
                mode === "ideas"
                  ? $t("startByEnteringChannelHandle")
                  : mode === "script"
                  ? $t("startBySelectingScriptTemplate")
                  : $t("pasteYouTubeVideoLinkBelow")
              }}
            </p>

            <v-text-field
              v-if="!['ideas', 'script'].includes(mode)"
              v-model="options.videoUrl"
              filled
              rounded
              :label="$t('videoLink')"
              :rules="[rules.required, rules.validateUrl]"
              class="mt-6"
            >
              <template v-slot:prepend-inner>
                <v-icon class="mr-2">mdi-link-variant</v-icon>
              </template>
              <template v-slot:append>
                <v-btn text outlined rounded small color="primary" @click="languageSelect = true">{{
                  shortStore.language
                }}</v-btn>
              </template>
            </v-text-field>

            <v-combobox
              v-if="mode === 'ideas'"
              v-model="options.channelHandle"
              filled
              rounded
              :label="$t('channelHandle')"
              :rules="[rules.required]"
              single-line
              append-icon=""
              placeholder="@MrBeast"
              @input="onHandleInput"
              class="mt-6"
            >
              <template v-slot:selection="data">
                <v-chip
                  :key="JSON.stringify(data.item)"
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  :disabled="data.disabled"
                  @click:close="data.parent.selectItem(data.item)"
                >
                  <v-avatar height="5" class="primary white--text" left>{{
                    data.item.slice(1, 2).toUpperCase()
                  }}</v-avatar>
                  {{ data.item }}
                </v-chip>
              </template>
            </v-combobox>

            <v-select
              v-if="mode === 'script'"
              v-model="options.scriptTemplate"
              :menu-props="{
                transition: 'slide-y-transition',
                'offset-y': true,
                rounded: 'lg',
              }"
              :label="$t('scriptTemplate')"
              :items="[
                { value: 'general', text: $t('generalScript') },
                { value: 'short', text: $t('short') },
                { value: 'outline', text: $t('scriptOutline') },
              ]"
              :rules="[rules.required]"
              rounded
              filled
              class="mt-6"
            >
            </v-select>

            <div
              v-if="!['ideas', 'rewriteScript', 'tags', 'chapters'].includes(mode)"
              class="d-flex align-center justify-space-between"
            >
              <h3 class="mr-2">{{ $t("giveUsMoreContext") }}</h3>
              <v-tooltip
                v-if="
                  ['ideas', 'title', 'description'].includes(mode) ||
                  (mode === 'script' && options.scriptTemplate === 'general')
                "
                bottom
                max-width="500"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on">mdi-help-circle</v-icon>
                </template>
                <span>{{
                  mode === "script"
                    ? $t("moreContextHintScript")
                    : mode === "title"
                    ? $t("moreContextHintTitle")
                    : $t("moreContextHintDescription")
                }}</span>
              </v-tooltip>
            </div>

            <v-row v-if="!['ideas', 'rewriteScript', 'tags', 'chapters'].includes(mode)" dense class="mt-3">
              <v-col v-if="mode === 'script' && options.scriptTemplate === 'general'" cols="12">
                <v-text-field
                  v-model="options.videoUrl"
                  filled
                  rounded
                  :label="$t('linkToYourExistingVideo')"
                  :rules="[rules.validateUrl]"
                  hide-details
                  class="mb-2"
                >
                  <template v-slot:prepend-inner>
                    <v-icon class="mr-2">mdi-link-variant</v-icon>
                  </template>
                  <template v-slot:append>
                    <v-btn text outlined rounded small color="primary" @click="languageSelect = true">{{
                      shortStore.language
                    }}</v-btn>
                  </template>
                </v-text-field>
              </v-col>

              <v-col v-if="mode === 'script'" cols="12">
                <v-slider
                  v-model="options.numberOfWords"
                  :disabled="options.scriptTemplate === 'short'"
                  :label="$t('numberOfWords')"
                  :hint="$t('approximatelyXMinutes', { minutes: wordsToSpeakingMinutes(options.numberOfWords) })"
                  min="200"
                  max="4400"
                  step="100"
                  persistent-hint
                  thumb-label
                >
                  <template v-slot:thumb-label="{ value }">
                    {{ abbreviateNumber(value) }}
                  </template></v-slider
                >
              </v-col>

              <v-col v-if="mode === 'title'" cols="12" :md="mode === 'description' ? 12 : 8">
                <v-combobox
                  v-model="options.inspirationChannelHandles"
                  filled
                  rounded
                  :label="$t('inspirationChannelHandles')"
                  multiple
                  single-line
                  append-icon=""
                  placeholder="@MrBeast"
                  @input="onHandlesInput"
                  hint="x"
                  :counter="$vuetify.breakpoint.smAndDown ? null : 3"
                  :hide-details="$vuetify.breakpoint.smAndDown"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      :key="JSON.stringify(data.item)"
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      :disabled="data.disabled"
                      @click:close="data.parent.selectItem(data.item)"
                    >
                      <v-avatar height="5" class="primary white--text" left>{{
                        data.item.slice(1, 2).toUpperCase()
                      }}</v-avatar>
                      {{ data.item }}
                    </v-chip>
                  </template>
                  <template v-slot:message>
                    <span v-html="$t('channelHandlesHint')"></span>
                  </template>
                </v-combobox>
              </v-col>

              <v-col v-if="mode === 'description'" cols="12" :md="mode === 'description' ? 12 : 8">
                <v-combobox
                  v-model="options.channelHandle"
                  filled
                  rounded
                  :label="$t('channelHandle')"
                  single-line
                  append-icon=""
                  placeholder="@MrBeast"
                  @input="onHandleInput"
                >
                  <template v-slot:selection="data">
                    <v-chip
                      :key="JSON.stringify(data.item)"
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      :disabled="data.disabled"
                      @click:close="data.parent.selectItem(data.item)"
                    >
                      <v-avatar height="5" class="primary white--text" left>{{
                        data.item.slice(1, 2).toUpperCase()
                      }}</v-avatar>
                      {{ data.item }}
                    </v-chip>
                  </template>
                </v-combobox>
              </v-col>

              <v-col v-if="!['script', 'description'].includes(mode)" cols="12" md="4">
                <v-select
                  v-model="options.trendingInRegion"
                  :items="youtubeRegions"
                  item-value="code"
                  item-text="name"
                  :menu-props="{
                    transition: 'slide-y-transition',
                    'offset-y': true,
                    rounded: 'lg',
                  }"
                  :label="$t('trendingInCountry')"
                  rounded
                  filled
                  hide-details
                >
                </v-select>
              </v-col>
            </v-row>
          </div>

          <div v-else-if="chatStore.loadingChatId === $route.params.chatId && chatStore.fetchingChat">
            <v-skeleton-loader class="message-bubble ml-auto mb-4" max-width="350" type="heading"></v-skeleton-loader>
            <v-skeleton-loader class="message-bubble ml-auto mb-8" max-width="255" type="heading"></v-skeleton-loader>
            <v-skeleton-loader class="mr-auto mb-2" max-width="450" type="heading"></v-skeleton-loader>
            <v-skeleton-loader class="mr-auto mb-2" max-width="275" type="heading"></v-skeleton-loader>
            <v-skeleton-loader class="mr-auto mb-2" max-width="375" type="heading"></v-skeleton-loader>
            <v-skeleton-loader class="mr-auto mb-2" max-width="250" type="heading"></v-skeleton-loader>
            <v-skeleton-loader class="mr-auto mb-2" max-width="300" type="heading"></v-skeleton-loader>
          </div>

          <ChatMessages
            v-else
            :mode="mode"
            :options="options"
            :messages="messages"
            :waitingForAssistantMessage="chatStore.waitingForAssistantMessage"
            :progressMessage="progressMessage"
            @reply="replyWithSelection($event)"
          />

          <div ref="bottomElement"></div>
        </div>

        <div class="chat-input d-flex align-end justify-center pt-0 pb-2">
          <div class="flex-grow-1 mr-2">
            <div v-if="replyText" class="reply-wrapper">
              <v-alert icon="mdi-arrow-right-bottom" dense class="mb-0 body-2" dismissible @input="toggleAlert($event)">
                {{ truncateText(replyText) }}
              </v-alert>
            </div>
            <div v-else-if="suggestedActions.length > 0 && !message" class="reply-wrapper d-flex overflow-x-auto">
              <button
                v-for="(action, idx) in suggestedActions"
                :key="idx"
                class="suggested-action-box mr-2 d-flex align-center"
                @click="message = action.content"
              >
                <v-icon class="mr-4">mdi-shimmer</v-icon>
                <span class="body-2">{{ action.name }}</span>
              </button>
            </div>
            <div class="textarea-btn-wrapper">
              <v-textarea
                :key="suggestedActions.length > 0"
                v-model="message"
                :rules="mode === 'script' || !!chatId ? [rules.required, rules.maxLength] : [rules.maxLength]"
                :placeholder="
                  chatId || chatStore.waitingForAssistantMessage
                    ? $t('askAnything')
                    : mode === 'script'
                    ? $t('whatShouldYourVideoBeAbout')
                    : $t('provideAdditionalContext')
                "
                :disabled="!!chatStore.loadingChatId || chatStore.waitingForAssistantMessage"
                class="pa-0"
                :auto-grow="messageLineCount < ($vuetify.breakpoint.xs ? 5 : 10)"
                :rows="textareaRows"
                rounded
                filled
                hide-details
                :style="replyText || (suggestedActions.length > 0 && !message) ? 'border-radius: 0 0 28px 28px;' : null"
              >
              </v-textarea>
              <v-btn
                :disabled="!valid || !!chatStore.loadingChatId || chatStore.waitingForAssistantMessage"
                :loading="chatStore.waitingForAssistantMessage"
                small
                fab
                color="primary"
                @click="chatId ? sendMessage() : initChat()"
              >
                <v-icon>mdi-arrow-up</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </v-form>
    </v-container>

    <v-bottom-sheet v-if="$vuetify.breakpoint.mobile" inset max-width="600px" scrollable v-model="languageSelect">
      <LanguageSelect @close-dialog="languageSelect = false" />
    </v-bottom-sheet>
    <v-dialog v-else v-model="languageSelect" max-width="500">
      <LanguageSelect @close-dialog="languageSelect = false" />
    </v-dialog>
  </div>
</template>

<script>
import { useAccountStore } from "@/stores/account";
import { useSnackbarStore } from "@/stores/snackbar";
import { useShortStore } from "@/stores/short";
import { useChatStore } from "@/stores/chat";
import { useUpgradeStore } from "@/stores/upgrade";
import LanguageSelect from "@/components/search/LanguageSelect.vue";
import ChatMessages from "@/components/ChatMessages.vue";
import { getApiUrl } from "@/api.js";
import { SSE } from "sse.js";
import prettyMilliseconds from "pretty-ms";
import { deepCopy } from "@/util/helpers";

export default {
  name: "ChatView",
  components: { LanguageSelect, ChatMessages },
  setup() {
    const accountStore = useAccountStore();
    const shortStore = useShortStore();
    const snackbarStore = useSnackbarStore();
    const chatStore = useChatStore();
    const upgradeStore = useUpgradeStore();
    return { accountStore, shortStore, snackbarStore, chatStore, upgradeStore };
  },
  data() {
    return {
      valid: true,
      rules: {
        required: (v) => !!v || this.$t("requiredField"),
        maxLength: (v) => v.length <= 5000 || this.$t("maxChars", { chars: 5000 }),
        validateUrl: (v) => {
          const isYouTube = /^https:\/\/(?:www\.)?youtu(?:be\.com\/watch\?v=|\.be\/)([\w\-_]*)(&(amp;)?[\w?=]*)?/.test(
            v.trim()
          );
          const isGoogleDrive = /^https:\/\/drive\.google\.com\/file\/d\/[\w-]+/.test(v.trim());
          const isGoogleDriveDirectUrl =
            /^https:\/\/(?:drive\.google\.com\/uc|drive\.usercontent\.google\.com\/download)\?id=[^&]+&export=download&confirm=t$/.test(
              v.trim()
            );
          const isDirectVideoUrl =
            /^(http(s):\/\/.)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)\.(mp4|mov|webm)$/.test(
              v.trim()
            );
          return (
            v === "" ||
            isYouTube ||
            isGoogleDrive ||
            isGoogleDriveDirectUrl ||
            isDirectVideoUrl ||
            this.$t("invalidVideoLink")
          );
        },
      },
      languageSelect: false,
      youtubeRegions: JSON.parse(
        '[{"name":"Algeria","code":"DZ"},{"name":"Argentina","code":"AR"},{"name":"Australia","code":"AU"},{"name":"Austria","code":"AT"},{"name":"Azerbaijan","code":"AZ"},{"name":"Bahrain","code":"BH"},{"name":"Bangladesh","code":"BD"},{"name":"Belarus","code":"BY"},{"name":"Belgium","code":"BE"},{"name":"Bolivia","code":"BO"},{"name":"Bosnia and Herzegovina","code":"BA"},{"name":"Brazil","code":"BR"},{"name":"Bulgaria","code":"BG"},{"name":"Cambodia","code":"KH"},{"name":"Canada","code":"CA"},{"name":"Chile","code":"CL"},{"name":"Colombia","code":"CO"},{"name":"Costa Rica","code":"CR"},{"name":"Croatia","code":"HR"},{"name":"Cyprus","code":"CY"},{"name":"Czechia","code":"CZ"},{"name":"Denmark","code":"DK"},{"name":"Dominican Republic","code":"DO"},{"name":"Ecuador","code":"EC"},{"name":"Egypt","code":"EG"},{"name":"El Salvador","code":"SV"},{"name":"Estonia","code":"EE"},{"name":"Finland","code":"FI"},{"name":"France","code":"FR"},{"name":"Georgia","code":"GE"},{"name":"Germany","code":"DE"},{"name":"Ghana","code":"GH"},{"name":"Greece","code":"GR"},{"name":"Guatemala","code":"GT"},{"name":"Honduras","code":"HN"},{"name":"Hong Kong","code":"HK"},{"name":"Hungary","code":"HU"},{"name":"Iceland","code":"IS"},{"name":"India","code":"IN"},{"name":"Indonesia","code":"ID"},{"name":"Iraq","code":"IQ"},{"name":"Ireland","code":"IE"},{"name":"Israel","code":"IL"},{"name":"Italy","code":"IT"},{"name":"Jamaica","code":"JM"},{"name":"Japan","code":"JP"},{"name":"Jordan","code":"JO"},{"name":"Kazakhstan","code":"KZ"},{"name":"Kenya","code":"KE"},{"name":"Kuwait","code":"KW"},{"name":"Laos","code":"LA"},{"name":"Latvia","code":"LV"},{"name":"Lebanon","code":"LB"},{"name":"Libya","code":"LY"},{"name":"Liechtenstein","code":"LI"},{"name":"Lithuania","code":"LT"},{"name":"Luxembourg","code":"LU"},{"name":"Malaysia","code":"MY"},{"name":"Malta","code":"MT"},{"name":"Mexico","code":"MX"},{"name":"Moldova","code":"MD"},{"name":"Montenegro","code":"ME"},{"name":"Morocco","code":"MA"},{"name":"Nepal","code":"NP"},{"name":"Netherlands","code":"NL"},{"name":"New Zealand","code":"NZ"},{"name":"Nicaragua","code":"NI"},{"name":"Nigeria","code":"NG"},{"name":"North Macedonia","code":"MK"},{"name":"Norway","code":"NO"},{"name":"Oman","code":"OM"},{"name":"Pakistan","code":"PK"},{"name":"Panama","code":"PA"},{"name":"Papua New Guinea","code":"PG"},{"name":"Paraguay","code":"PY"},{"name":"Peru","code":"PE"},{"name":"Philippines","code":"PH"},{"name":"Poland","code":"PL"},{"name":"Portugal","code":"PT"},{"name":"Puerto Rico","code":"PR"},{"name":"Qatar","code":"QA"},{"name":"Romania","code":"RO"},{"name":"Russia","code":"RU"},{"name":"Saudi Arabia","code":"SA"},{"name":"Senegal","code":"SN"},{"name":"Serbia","code":"RS"},{"name":"Singapore","code":"SG"},{"name":"Slovakia","code":"SK"},{"name":"Slovenia","code":"SI"},{"name":"South Africa","code":"ZA"},{"name":"South Korea","code":"KR"},{"name":"Spain","code":"ES"},{"name":"Sri Lanka","code":"LK"},{"name":"Sweden","code":"SE"},{"name":"Switzerland","code":"CH"},{"name":"Taiwan","code":"TW"},{"name":"Tanzania","code":"TZ"},{"name":"Thailand","code":"TH"},{"name":"Tunisia","code":"TN"},{"name":"Turkey","code":"TR"},{"name":"Uganda","code":"UG"},{"name":"Ukraine","code":"UA"},{"name":"United Arab Emirates","code":"AE"},{"name":"United Kingdom","code":"GB"},{"name":"United States","code":"US"},{"name":"Uruguay","code":"UY"},{"name":"Venezuela","code":"VE"},{"name":"Vietnam","code":"VN"},{"name":"Yemen","code":"YE"},{"name":"Zimbabwe","code":"ZW"}]'
      ),
      chatId: null,
      mode: "title",
      options: {
        videoUrl: "",
        language: this.shortStore.language,
        channelHandle: this.accountStore.account?.info?.youtubeChannelHandle || null,
        inspirationChannelHandles: this.accountStore.account?.info?.youtubeChannelHandle
          ? [this.accountStore.account?.info?.youtubeChannelHandle]
          : null,
        trendingInRegion: null,
        scriptTemplate: "general",
        numberOfWords: 700,
      },
      messages: [],
      message: "",
      replyText: "",
      progressMessage: "",
    };
  },
  computed: {
    messageLineCount() {
      return (this.message.match(/\n/g) || []).length;
    },
    suggestedActions() {
      if (this.chatId === null) return [];
      if (this.mode === "ideas")
        return [{ name: this.$t("generateNewVideoIdeas"), content: this.$t("generateNewVideoIdeasMessage") }];
      if (this.mode === "script" && this.options.scriptTemplate === "general")
        return [
          {
            name: this.$t("makeScriptHookMorePowerful"),
            content: this.$t("makeScriptHookMorePowerfulMessage"),
          },
          { name: this.$t("generateScriptOutline"), content: this.$t("generateScriptOutlineMessage") },
          { name: this.$t("shortenScript"), content: this.$t("shortenScriptMessage") },
        ];
      if (this.mode === "script" && this.options.scriptTemplate === "short")
        return [
          { name: this.$t("makeScriptLonger"), content: this.$t("makeScriptLongerMessage") },
          { name: this.$t("makeShortPunchier"), content: this.$t("makeShortPunchierMessage") },
        ];
      if (this.mode === "script" && this.options.scriptTemplate === "outline")
        return [
          { name: this.$t("makeScriptOutlineMoreDetailed"), content: this.$t("makeScriptOutlineMoreDetailedMessage") },
        ];
      if (this.mode === "rewriteScript")
        return [{ name: this.$t("makeScriptMoreOriginal"), content: this.$t("makeScriptMoreOriginalMessage") }];
      if (this.mode === "title")
        return [
          { name: this.$t("generateNewTitles"), content: this.$t("generateNewTitlesMessage") },
          { name: this.$t("makeTitlesMoreViral"), content: this.$t("makeTitlesMoreViralMessage") },
          { name: this.$t("shortenTitles"), content: this.$t("shortenTitlesMessage") },
        ];
      if (this.mode === "description")
        return [
          {
            name: this.$t("makeDescriptionMoreDetailed"),
            content: this.$t("makeDescriptionMoreDetailedMessage"),
          },
          {
            name: this.$t("shortenDescription"),
            content: this.$t("shortenDescriptionMessage"),
          },
        ];
      if (this.mode === "chapters")
        return [
          {
            name: this.$t("renameChaptersForClarity"),
            content: this.$t("renameChaptersForClarityMessage"),
          },
        ];
      if (this.mode === "tags")
        return [
          {
            name: this.$t("generateNewRelevantTags"),
            content: this.$t("generateNewRelevantTagsMessage"),
          },
        ];
      return [];
    },
    textareaRows() {
      const maxRows = this.$vuetify.breakpoint.xs ? 5 : 10;
      if (this.messageLineCount >= maxRows) return maxRows;
      if (this.suggestedActions.length > 0 && !this.message) return 2;
      return 3;
    },
  },
  created() {
    this.shortStore.language = "auto";
    const language = localStorage.language;
    if (
      language &&
      [
        "bg",
        "cs",
        "da",
        "nl",
        "en",
        "fr",
        "fi",
        "de",
        "el",
        "hu",
        "id",
        "it",
        "ja",
        "ko",
        "lv",
        "lt",
        "no",
        "pl",
        "pt",
        "ro",
        "ru",
        "sk",
        "es",
        "sv",
        "tr",
        "uk",
      ].includes(language)
    )
      this.shortStore.language = language;

    if (this.$route.query.mode) this.mode = this.$route.query.mode;
    if (this.$route.params.chatId === "new") this.chatStore.initNewChat++;
    else this.loadChat(this.$route.params.chatId);
  },
  watch: {
    "$route.params.chatId"(chatId) {
      if (chatId !== "new") this.loadChat(chatId);
      else this.chatStore.initNewChat++;
    },
    "chatStore.initNewChat"() {
      if (this.$route.params.chatId !== "new") this.$router.push(`/chat/new?mode=${this.mode}`);
      this.chatId = null;
      this.options = {
        videoUrl: "",
        language: this.shortStore.language,
        channelHandle: this.accountStore.account?.info?.youtubeChannelHandle || null,
        inspirationChannelHandles: this.accountStore.account?.info?.youtubeChannelHandle
          ? [this.accountStore.account?.info?.youtubeChannelHandle]
          : null,
        trendingInRegion: null,
        scriptTemplate: this.$route.query?.template || "general",
        numberOfWords: 700,
      };
      this.messages = [];
      this.message = "";
      this.replyText = "";
      this.chatStore.waitingForAssistantMessage = false;
      this.progressMessage = "";
    },
    inspirationChannelHandles: {
      immediate: false,
      handler() {
        this.processHandles();
      },
    },
  },
  methods: {
    async initChat() {
      this.chatStore.waitingForAssistantMessage = true;
      this.progressMessage = "";

      const options = deepCopy(this.options);
      options.videoUrl = encodeURIComponent(options.videoUrl);
      let url = `${getApiUrl()}/chats/new?mode=${this.mode}&message=${encodeURIComponent(
        this.message
      )}&options=${JSON.stringify(options)}`;

      const message = { content: this.message };
      if (this.message) this.messages.push({ role: "user", ...message });
      setTimeout(() => this.scrollToBottom(), 100);
      this.message = "";

      let sse = new SSE(url, { withCredentials: true, headers: { "x-csrf-token": localStorage.getItem("csrfToken") } });

      sse.onmessage = (e) => {
        const data = JSON.parse(e.data);
        const eventName = data.event;

        switch (eventName) {
          case "transcript-fetching-started":
            this.progressMessage = `${this.$t("fetchingTranscript")}...`;
            break;
          case "audio-downloading-started":
            this.progressMessage = `${this.$t("downloadingVideosAudioForTranscription")}...`;
            break;
          case "video-transcribing-started":
            this.progressMessage = `${this.$t("transcribingVideo")}...`;
            break;
          case "video-transcribing-estimated-processing-time":
            this.progressMessage = `${this.$t("transcribingVideo")} (~${prettyMilliseconds(
              data.data.seconds * 1000
            )})...`;
            break;
          case "gathering-context-started":
            this.progressMessage = `${this.$t("gatheringContext")}...`;
            break;
          case "generating-started":
            this.progressMessage = `${this.$t("generating")}...`;
            break;
          case "chat-created":
            sse.close();
            this.chatStore.waitingForAssistantMessage = false;
            this.progressMessage = "";
            this.chatStore.processNewChatData(data.data);
            this.loadChat(data.data.chat._id, false);
            break;
          case "error":
            sse.close();
            this.chatStore.waitingForAssistantMessage = false;
            this.progressMessage = "";
            this.message = message?.content;
            if (this.message && this.messages[this.messages.length - 1].content === this.message) this.messages.pop();
            this.snackbarStore.text = this.$t("somethingWentWrong");
            switch (data.error) {
              case "USAGE_QUOTA_REACHED":
                if (this.accountStore.accountTier === "starter") {
                  this.upgradeStore.open(
                    this.$t("youveAlreadyUsedThisToolThisMonth"),
                    this.$t("timeToGiveYourAccountBoost"),
                    `${this.$t("youJustReachedChatUsageQuotaStarter")} ${this.$t(
                      "toContinueWithCreatingChatPleaseUpgradeToLite"
                    )}`
                  );
                } else {
                  this.snackbarStore.text = this.$t("usageQuotaReachedUpgradeToHigherPlan");
                  this.snackbarStore.snackbar = true;
                }
                break;
              case "TRANSCRIBE_USAGE_QUOTA_REACHED":
                if (this.accountStore.accountTier === "starter") {
                  this.upgradeStore.open(
                    this.$route.query.googleDriveFileId
                      ? this.$t("unlockGoogleDriveImports")
                      : this.$t("unlockPublicUrlImports"),
                    this.$t("timeToGiveYourAccountBoost"),
                    `${this.$t("importingPremiumFeature")} ${this.$t("toContinueWithCreatingChatPleaseUpgradeToLite")}`
                  );
                } else {
                  this.snackbarStore.text = this.$t("transcribeUsageQuotaReachedUpgradeToHigherPlan");
                  this.snackbarStore.snackbar = true;
                }
                break;
              case "AUDIO_DOWNLOADING_FAILED":
                this.snackbarStore.text = this.$t("failedToDownloadAudio");
                this.snackbarStore.snackbar = true;
                break;
              case "GATED_FEATURE_PLEASE_LOGIN":
                this.snackbarStore.text = this.$t("chatRequiresLogIn");
                this.snackbarStore.snackbar = true;
                break;
              case "TRANSCRIBE_ERROR":
                this.snackbarStore.text = this.$t("transcribeErrorMakeSureVideoIsAccessible");
                this.snackbarStore.snackbar = true;
                break;
              case "NO_CAPTIONS_FOUND_FOR_PROVIDED_VIDEO":
                this.upgradeStore.open(
                  this.$t("noCaptionsFound"),
                  this.$t("doNotWorryWeCanTranscribe"),
                  `${this.$t("transcribingPremiumFeature")} ${this.$t("toContinueWithTranscribingPleaseUpgradeToLite")}`
                );
                break;
              case "NO_CAPTIONS_FOUND_FOR_PROVIDED_LANGUAGE":
                this.snackbarStore.text = this.$t("failedToAnalyzeNoCaptionsForLanguage", {
                  language: this.shortStore.language,
                });
                this.snackbarStore.snackbar = true;
                break;
              case "YOUTUBE_VIDEO_UNAVAILABLE":
                this.snackbarStore.text = this.$t("youtubeVideoUnavailable");
                this.snackbarStore.snackbar = true;
                break;
              case "YOUTUBE_PREMIERS_NOT_SUPPORTED":
                this.snackbarStore.text = this.$t("youtubePremieresNotSupported");
                this.snackbarStore.snackbar = true;
                break;
              case "YOUTUBE_AGE_RESTRICTED_VIDEOS_NOT_SUPPORTED":
                this.snackbarStore.text = this.$t("youtubeAgeRestrictedVideosNotSupported");
                this.snackbarStore.snackbar = true;
                break;
              case "YOUTUBE_PRIVATE_VIDEOS_NOT_SUPPORTED":
                this.snackbarStore.text = this.$t("youtubePrivateVideosNotSupported");
                this.snackbarStore.snackbar = true;
                break;
              case "YOUTUBE_VIDEO_BEING_PROCESSED":
                this.snackbarStore.text = this.$t("youtubeVideoStillBeingProcessed");
                this.snackbarStore.snackbar = true;
                break;
              case "MUSIC_VIDEOS_NOT_SUPPORTED":
                this.snackbarStore.text = this.$t("musicVideosNotSupported");
                this.snackbarStore.snackbar = true;
                break;
              case "CAPTIONS_LANGUAGE_NOT_SUPPORTED":
                this.snackbarStore.text = this.$t("languageNotSupportedYet", {
                  language: this.shortStore.language,
                });
                this.snackbarStore.snackbar = true;
                break;
              case "VIDEO_DURATION_TOO_SHORT":
                this.snackbarStore.text = this.$t("cannotBeAnalyzedDurationTooShort");
                this.snackbarStore.snackbar = true;
                break;
              case "AI_PROVIDER_REQUEST_ERROR":
                this.snackbarStore.text = this.$t("aiProviderOutagePleseTryAgainLater");
                this.snackbarStore.snackbar = true;
                break;
              case "GET_TRANSCRIPT_ERROR":
                this.snackbarStore.text = this.$t("failedToLoadVideoTranscript");
                this.snackbarStore.snackbar = true;
                break;
              case "YOUTUBE_CHANNEL_HANDLE_NOT_FOUND":
                this.snackbarStore.text = this.$t("youtubeChannelHandleNotFound");
                this.snackbarStore.snackbar = true;
                break;
              case "VIDEO_DURATION_TOO_LONG":
                if (!["script", "rewriteScript"].includes(this.mode)) {
                  this.upgradeStore.open(
                    this.$t("unlockUnlimitedVideoDuration"),
                    this.$t("timeToGiveYourAccountBoost"),
                    `${this.$t("removeXMinuteVideoDurationRestriction", { minutes: 30 })} ${this.$t(
                      "toContinueWithCreatingChatPleaseUpgradeToLite"
                    )}`
                  );
                } else {
                  this.snackbarStore.text = this.$t("videoDurationTooLong", { minutes: 30 });
                  this.snackbarStore.snackbar = true;
                }
                break;
              default:
                this.snackbarStore.snackbar = true;
            }
        }
      };

      sse.onerror = (err) => {
        sse.close();
        this.chatStore.waitingForAssistantMessage = false;
        this.progressMessage = "";
        this.message = message?.content;
        if (this.messages.length && this.messages[this.messages.length - 1].content === this.message)
          this.messages.pop();
        if (err?.responseCode === 401) this.snackbarStore.text = this.$t("chatRequiresLogIn");
        else this.snackbarStore.text = this.$t("somethingWentWrong");
        this.snackbarStore.snackbar = true;
      };
    },
    async loadChat(chatId, fetchChat = true) {
      try {
        if (this.chatId === chatId) return;

        this.chatStore.loadingChatId = chatId;

        this.chatId = chatId;
        if (fetchChat) await this.chatStore.fetchChat(chatId);
        else if (this.$route.params.chatId === "new") this.$router.push(`/chat/${chatId}`);
        this.mode = this.chatStore.chat.mode;
        this.messages = this.chatStore.chat.messages;
        this.options = this.chatStore.chat.options;
        setTimeout(() => this.scrollToBottom(), 250);
      } catch (err) {
        this.snackbarStore.text = this.$t("failedToLoadChat");
        this.snackbarStore.snackbar = true;
      }
      this.chatStore.loadingChatId = null;
      this.chatStore.waitingForAssistantMessage = false;
      this.progressMessage = "";
    },
    async sendMessage() {
      let message;
      try {
        this.chatStore.waitingForAssistantMessage = true;
        this.progressMessage = `${this.$t("generating")}...`;
        message = { content: this.message, reply: this.replyText };
        this.messages.push({ role: "user", ...message });
        setTimeout(() => this.scrollToBottom(), 50);
        this.message = "";
        this.replyText = "";
        const resp = await this.chatStore.sendMessage(this.chatId, message);
        this.messages.push(resp.data.message);
        setTimeout(() => this.scrollToBottom(), 100);
      } catch (error) {
        this.chatStore.waitingForAssistantMessage = false;
        this.progressMessage = "";
        this.message = message?.content;
        this.replyText = message?.reply;
        if (this.messages[this.messages.length - 1].content === this.message) this.messages.pop();

        if (error.response.data === "MAX_MESSAGES_COUNT_REACHED") {
          this.upgradeStore.open(
            this.$t("unlockUnlimitedChatMessages"),
            this.$t("timeToGiveYourAccountBoost"),
            `${this.$t("removeXChatMessagesRestriction", { messages: 5 })} ${this.$t(
              "toContinueWithChattingPleaseUpgradeToLite"
            )}`
          );
        } else if (error.response.data === "AI_PROVIDER_REQUEST_ERROR") {
          this.snackbarStore.text = this.$t("aiProviderOutagePleseTryAgainLater");
          this.snackbarStore.snackbar = true;
        } else {
          this.snackbarStore.text = this.$t("somethingWentWrong");
          this.snackbarStore.snackbar = true;
        }
      }
      this.chatStore.waitingForAssistantMessage = false;
      this.progressMessage = "";
    },
    processHandle() {
      let handle = this.options.channelHandle.trim();

      // Add '@' if missing and remove all spaces
      if (!handle.startsWith("@")) {
        handle = "@" + handle;
      }
      handle = handle.replace(/\s+/g, "");

      // Remove if still contains spaces (which shouldn't after replace)
      if (/\s/.test(handle)) {
        handle = "";
      }

      if (handle !== this.options.channelHandle) {
        this.options.channelHandle = handle;
      }
    },
    processHandles() {
      let handles = [...this.options.inspirationChannelHandles];
      handles = handles.map((h) => h.trim()).filter((h, i, arr) => arr.indexOf(h) === i);

      if (handles.length > 3) {
        handles = handles.slice(0, 3);
      }

      handles = handles.map((h) => (h.startsWith("@") ? h : "@" + h).replace(/\s+/g, ""));
      let hasInvalid = handles.some((h) => /\s/.test(h));
      if (hasInvalid) {
        handles = handles.filter((h) => !/\s/.test(h));
      }

      if (JSON.stringify(handles) !== JSON.stringify(this.options.inspirationChannelHandles)) {
        this.options.inspirationChannelHandles = handles;
      }
    },
    onHandleInput(newVal) {
      this.options.channelHandle = newVal;
      if (this.options.channelHandle) this.processHandle();
    },
    onHandlesInput(newVal) {
      this.options.inspirationChannelHandles = newVal;
      this.processHandles();
    },
    replyWithSelection(text) {
      this.replyText = text;
      // setTimeout(() => this.scrollToBottom(), 50);
    },
    toggleAlert(e) {
      if (e === false) this.replyText = null;
    },
    truncateText(str) {
      const charLimit = this.$vuetify.breakpoint.xs ? 72 : 300;
      if (str.length <= charLimit) return str;
      else return str.slice(0, charLimit).trim() + "...";
    },
    wordsToSpeakingMinutes(numberOfWords) {
      const count = Number(numberOfWords) || 0;
      return +(count / 145).toFixed();
    },
    abbreviateNumber(num) {
      if (num >= 1000) {
        let formatted = (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1);
        return `${formatted}k`;
      }
      return num.toString();
    },
    scrollToBottom() {
      this.$refs.bottomElement?.scrollIntoView({ behavior: "smooth" });
    },
  },
};
</script>

<style scoped>
/* Make the overall chat UI take the full space */
.chat-bot-wrapper {
  display: flex;
  flex-direction: column;
}

/* v-container style override to stretch */
.chat-bot-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* allow flex children to shrink */
}

.chat-content {
  flex: 1 1 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* for smoother scroll and spacing */
  min-height: 0;
  max-height: 100%;
}

:deep(.v-text-field--enclosed .v-input__append-inner) {
  margin-top: 15px;
}

:deep(.v-dialog),
:deep(.v-dialog .v-card) {
  border-radius: 16px;
}

:deep(.v-bottom-sheet),
:deep(.v-bottom-sheet .v-card) {
  border-radius: 16px 16px 0 0;
}

.reply-wrapper {
  border-radius: 28px 28px 0 0;
  padding: 0 12px;
  padding-top: 12px;
}

.theme--light .reply-wrapper {
  background: rgba(0, 0, 0, 0.06);
}

.theme--dark .reply-wrapper {
  background: hsla(0, 0%, 100%, 0.08);
}

.reply-wrapper .v-alert {
  border-radius: 28px;
}

.suggested-action-box {
  flex: none;
  height: 40px;
  padding-left: 16px;
  padding-right: 16px;
  border-radius: 28px;
}

.theme--light .suggested-action-box {
  background: white;
}

.theme--dark .suggested-action-box {
  background: #1e1e1e;
}

.textarea-btn-wrapper {
  position: relative;
}

.textarea-btn-wrapper .v-btn {
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 2;
  box-shadow: none;
}

:deep(
    .theme--light.v-text-field--filled:not(.v-input--is-focused):not(.v-input--has-state)
      > .v-input__control
      > .v-input__slot:hover
  ) {
  background: rgba(0, 0, 0, 0.06);
}

:deep(
    .theme--dark.v-text-field--filled:not(.v-input--is-focused):not(.v-input--has-state)
      > .v-input__control
      > .v-input__slot:hover
  ) {
  background: hsla(0, 0%, 100%, 0.08);
}

:deep(.v-skeleton-loader__heading) {
  width: 100%;
}

.message-bubble :deep(.v-skeleton-loader__heading) {
  height: 40px;
  border-radius: 24px;
}

.boujee-text {
  --bg-size: 400%;
  --color-one: #0169ee;
  --color-two: #78b3fe;
  background: linear-gradient(90deg, var(--color-one), var(--color-two), var(--color-one)) 0 0 / var(--bg-size) 100%;
  color: transparent;
  background-clip: text;
}

@media (prefers-reduced-motion: no-preference) {
  .boujee-text {
    animation: move-bg 16s linear infinite;
  }
  @keyframes move-bg {
    to {
      background-position: var(--bg-size) 0;
    }
  }
}
</style>
